import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../api/client';
import { usePermission } from '../context/PermissionContext';
import { useSortable, usePagination } from '../hooks/useTableUtils';

const typeBadge = { end_user: 'badge-blue', distributor: 'badge-purple', partner: 'badge-teal' };
const typeLabel = { end_user: '고객', distributor: '총판', partner: '협력사' };
const emptyForm = { name:'', customer_type:'end_user', contact_name:'', phone:'', email:'' };

export default function CustomersPage() {
  const navigate = useNavigate();
  const { canPerformAction } = usePermission();
  const [customers, setCustomers] = useState([]);
  const [incomplete, setIncomplete] = useState([]);
  const [typeFilter, setTypeFilter] = useState('');
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [showDeleted, setShowDeleted] = useState(false);
  const [deletedCustomers, setDeletedCustomers] = useState([]);
  // 담당자별 견적 목록 모달
  const [estimatesFor, setEstimatesFor] = useState(null); // 고객 객체 or null
  const [estimatesList, setEstimatesList] = useState([]);
  const [estimatesLoading, setEstimatesLoading] = useState(false);
  const openEstimatesModal = async (c) => {
    setEstimatesFor(c); setEstimatesList([]); setEstimatesLoading(true);
    try { setEstimatesList(await api.get(`/customers/${c.id}/estimates`)); }
    catch (e) { alert(e.message); }
    finally { setEstimatesLoading(false); }
  };
  // 공유요청 모달
  const [shareReqTarget, setShareReqTarget] = useState(null); // { estimate_id, estimate_number, author_name }
  const [shareReqMsg, setShareReqMsg] = useState('');
  const [shareReqSubmitting, setShareReqSubmitting] = useState(false);
  const openShareRequest = (e) => {
    setShareReqTarget({ estimate_id: e.id, estimate_number: e.estimate_number || e.draft_id, author_name: e.author_name });
    setShareReqMsg('');
  };
  const submitShareRequest = async () => {
    if (!shareReqTarget) return;
    setShareReqSubmitting(true);
    try {
      await api.post('/share-requests', { estimate_id: shareReqTarget.estimate_id, message: shareReqMsg });
      alert('공유 요청이 전송되었습니다.');
      setShareReqTarget(null);
      // 목록 갱신해서 칩 상태 반영
      if (estimatesFor) {
        setEstimatesList(await api.get(`/customers/${estimatesFor.id}/estimates`));
      }
    } catch (e) { alert(e.message); }
    finally { setShareReqSubmitting(false); }
  };

  // 모달
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null); // null=신규, object=편집
  const [form, setForm] = useState({...emptyForm});
  const [formError, setFormError] = useState('');
  const [saving, setSaving] = useState(false);
  const [showDetail, setShowDetail] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deletePassword, setDeletePassword] = useState('');
  const [permanentTarget, setPermanentTarget] = useState(null);
  const [permanentPassword, setPermanentPassword] = useState('');

  // 체크박스
  const [selected, setSelected] = useState({});
  const [bulkDeletePw, setBulkDeletePw] = useState('');
  const [showBulkDelete, setShowBulkDelete] = useState(false);
  const [showBulkType, setShowBulkType] = useState(false);
  const [bulkTypeValue, setBulkTypeValue] = useState('end_user');
  const [showBulkPermanent, setShowBulkPermanent] = useState(false);
  const [bulkPermanentPw, setBulkPermanentPw] = useState('');
  const [selectedDeleted, setSelectedDeleted] = useState({});
  const [toast, setToast] = useState(null);

  useEffect(() => { loadData(); }, [typeFilter]);

  const loadData = async () => {
    try {
      const [custData, incData, delData] = await Promise.all([
        api.get(`/customers${typeFilter ? `?type=${typeFilter}` : ''}`),
        api.get('/customers/incomplete'),
        api.get('/customers?show_deleted=true'),
      ]);
      setCustomers(custData); setIncomplete(incData); setDeletedCustomers(delData);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  const showToast = (msg) => { setToast(msg); setTimeout(()=>setToast(null), 2500); };

  const openNew = () => { setEditing(null); setForm({...emptyForm}); setFormError(''); setShowForm(true); };
  const openEdit = (c) => { setEditing(c); setForm({ name:c.name||'', customer_type:c.customer_type||'end_user', contact_name:c.contact_name||'', phone:c.phone||'', email:c.email||'' }); setFormError(''); setShowForm(true); };

  const handleSave = async () => {
    if (!form.name) { setFormError('회사명은 필수입니다.'); return; }
    setSaving(true); setFormError('');
    try {
      if (editing) {
        await api.put(`/customers/${editing.id}`, form);
        showToast('고객 정보가 수정되었습니다.');
      } else {
        await api.post('/customers', form);
        showToast('고객이 등록되었습니다.');
      }
      setShowForm(false); loadData();
    } catch(e) { setFormError(e.message); }
    finally { setSaving(false); }
  };

  const handlePermanentDelete = async () => {
    if (!permanentPassword || !permanentTarget) return;
    try {
      await api.del(`/customers/${permanentTarget.id}/permanent`, { password: permanentPassword });
      showToast('완전 삭제되었습니다.');
      setPermanentTarget(null); setPermanentPassword('');
      loadData();
    } catch(e) { alert(e.message); }
  };

  const handleRestore = async (c) => {
    try {
      await api.post(`/customers/${c.id}/restore`);
      showToast('복원되었습니다.');
      loadData();
    } catch(e) { alert(e.message); }
  };

  const handleDelete = async () => {
    if (!deletePassword || !deleteTarget) return;
    try {
      await api.del(`/customers/${deleteTarget.id}`, { password: deletePassword });
      showToast('삭제 예정으로 전환되었습니다.');
      setDeleteTarget(null); setDeletePassword('');
      loadData();
    } catch(e) { alert(e.message); }
  };

  // 체크박스 헬퍼
  const displayList = customers.filter(c => !c.is_incomplete);
  const filteredForCheck = displayList.filter(c => { const q=(search||'').toLowerCase(); return !q||(c.name||'').toLowerCase().includes(q)||(c.contact_name||'').toLowerCase().includes(q); });
  const allSelected = filteredForCheck.length > 0 && filteredForCheck.every(c => selected[c.id]);
  const someSelected = filteredForCheck.some(c => selected[c.id]);
  const selectedCount = Object.values(selected).filter(Boolean).length;
  const selectedIds = Object.keys(selected).filter(k=>selected[k]).map(Number);
  const toggleAll = () => { if (allSelected) setSelected({}); else setSelected(Object.fromEntries(filteredForCheck.map(c=>[c.id,true]))); };
  const toggleOne = (id) => setSelected(prev=>({...prev,[id]:!prev[id]}));

  const allDeletedSelected = deletedCustomers.length > 0 && deletedCustomers.every(c=>selectedDeleted[c.id]);
  const someDeletedSelected = deletedCustomers.some(c=>selectedDeleted[c.id]);
  const selectedDeletedCount = Object.values(selectedDeleted).filter(Boolean).length;
  const selectedDeletedIds = Object.keys(selectedDeleted).filter(k=>selectedDeleted[k]).map(Number);
  const toggleAllDeleted = () => { if (allDeletedSelected) setSelectedDeleted({}); else setSelectedDeleted(Object.fromEntries(deletedCustomers.map(c=>[c.id,true]))); };
  const toggleOneDeleted = (id) => setSelectedDeleted(prev=>({...prev,[id]:!prev[id]}));

  // 일괄 즉시삭제
  const handleBulkPermanent = async () => {
    if (!bulkPermanentPw) return;
    try {
      for (const id of selectedDeletedIds) { await api.del(`/customers/${id}/permanent`, { password: bulkPermanentPw }); }
      showToast(`${selectedDeletedIds.length}건 완전 삭제되었습니다.`);
      setShowBulkPermanent(false); setBulkPermanentPw(''); setSelectedDeleted({});
      loadData();
    } catch(e) { alert(e.message); }
  };

  // 일괄 삭제
  const handleBulkDelete = async () => {
    if (!bulkDeletePw) return;
    try {
      for (const id of selectedIds) { await api.del(`/customers/${id}`, { password: bulkDeletePw }); }
      showToast(`${selectedIds.length}건 삭제 예정 처리되었습니다.`);
      setShowBulkDelete(false); setBulkDeletePw(''); setSelected({});
      loadData();
    } catch(e) { alert(e.message); }
  };

  // 일괄 유형 변경
  const handleBulkTypeChange = async () => {
    try {
      for (const id of selectedIds) { await api.put(`/customers/${id}`, { customer_type: bulkTypeValue }); }
      showToast(`${selectedIds.length}건 유형이 변경되었습니다.`);
      setShowBulkType(false); setSelected({});
      loadData();
    } catch(e) { alert(e.message); }
  };

  // 엑셀 내보내기
  const handleExportCSV = () => {
    const rows = selectedIds.length > 0 ? customers.filter(c=>selectedIds.includes(c.id)) : customers.filter(c=>!c.is_incomplete);
    const header = '회사명,유형,담당자,연락처,이메일';
    const csv = [header, ...rows.map(c=>[c.name,typeLabel[c.customer_type]||'',c.contact_name||'',c.phone||'',c.email||''].map(v=>`"${(v||'').replace(/"/g,'""')}"`).join(','))].join('\n');
    const blob = new Blob(['\uFEFF'+csv], {type:'text/csv;charset=utf-8;'});
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a'); a.href=url; a.download='고객목록.csv'; a.click(); URL.revokeObjectURL(url);
    showToast(`${rows.length}건 내보내기 완료`);
  };

  // 정렬 + 페이지네이션
  const activeCustomers = customers.filter(c => !c.is_incomplete);
  const { sorted: sortedCustomers, toggleSort, SortIcon } = useSortable(activeCustomers);
  const { Pager } = usePagination(sortedCustomers, 20);

  const filtered = customers.filter(c => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (c.name||'').toLowerCase().includes(q) || (c.contact_name||'').toLowerCase().includes(q);
  });

  return (
    <div>
      {toast && <div style={{position:'fixed',top:20,left:'50%',transform:'translateX(-50%)',zIndex:9999,background:'#1e293b',color:'#fff',padding:'10px 24px',borderRadius:8,fontSize:13,fontWeight:600,boxShadow:'0 4px 12px rgba(0,0,0,0.15)'}}>{toast}</div>}

      {incomplete.length > 0 && (
        <>
          <div className="alert alert-warning" style={{marginBottom:12}}>
            <strong>⚠ 세부정보 미입력 고객 {incomplete.length}건</strong> — 아래 고객의 세부정보를 입력해 주세요.
          </div>
          <div className="card" style={{marginBottom:20,borderColor:'#fde68a',background:'#fffdf7'}}>
            <div style={{overflowX:'auto'}}><table>
              <thead><tr><th>고객사명</th><th>담당자</th><th>등록일</th><th>견적건수</th><th></th></tr></thead>
              <tbody>
                {incomplete.map(c => (
                  <tr key={c.id}>
                    <td className="fw-bold">{c.name}</td>
                    <td>{c.contact_name || '-'}</td>
                    <td>{c.created_at?.slice(0,10)}</td>
                    <td>{c.estimate_count || 0}</td>
                    <td><button className="btn btn-primary btn-sm" onClick={()=>openEdit(c)}>세부정보 입력</button></td>
                  </tr>
                ))}
              </tbody>
            </table></div>
          </div>
        </>
      )}

      <div className="toolbar">
        <div style={{display:'flex',gap:8,alignItems:'center'}}>
          <div className="search-box"><span>🔍</span><input placeholder="고객명, 담당자 검색..." value={search} onChange={e=>setSearch(e.target.value)} /></div>
          <select className="form-input" style={{width:130,padding:'7px 10px',fontSize:12}} value={typeFilter} onChange={e=>setTypeFilter(e.target.value)}>
            <option value="">전체 유형</option>
            <option value="end_user">고객</option>
            <option value="distributor">총판</option>
            <option value="partner">협력사</option>
          </select>
        </div>
        <div className="btn-group">
          {someSelected && <>
            {canPerformAction('고객 삭제') && <button className="btn btn-danger btn-sm" onClick={()=>{setShowBulkDelete(true);setBulkDeletePw('');}}>일괄 삭제 ({selectedCount})</button>}
            {canPerformAction('고객 등록/편집') && <button className="btn btn-outline btn-sm" onClick={()=>{setShowBulkType(true);setBulkTypeValue('end_user');}}>유형 변경 ({selectedCount})</button>}
          </>}
          <button className="btn btn-outline btn-sm" onClick={handleExportCSV}>{selectedCount>0?`내보내기 (${selectedCount})`:'전체 내보내기'}</button>
          {deletedCustomers.length > 0 && <button className={`btn btn-sm ${showDeleted?'btn-primary':'btn-outline'}`} onClick={()=>setShowDeleted(!showDeleted)}>삭제예정 ({deletedCustomers.length})</button>}
          {canPerformAction('고객 등록/편집') && <button className="btn btn-primary btn-sm" onClick={openNew}>✚ 고객 등록</button>}
        </div>
      </div>

      <div className="card" style={{padding:0,overflow:'hidden'}}>
        {loading ? <div style={{padding:40,textAlign:'center'}}>로딩 중...</div> : (
          <div style={{overflowX:'auto'}}>
            <table>
              <thead><tr><th style={{width:36}}><input type="checkbox" checked={allSelected} onChange={toggleAll} /></th><th onClick={()=>toggleSort('name')}>회사명 <SortIcon col="name"/></th><th>유형</th><th>담당자</th><th>연락처</th><th style={{textAlign:'center'}} onClick={()=>toggleSort('estimate_count')}>견적수 <SortIcon col="estimate_count"/></th><th onClick={()=>toggleSort('last_activity')}>최근 활동 <SortIcon col="last_activity"/></th><th>작업</th></tr></thead>
              <tbody>
                {filtered.filter(c=>!c.is_incomplete).map(c=>(
                  <tr key={c.id}>
                    <td><input type="checkbox" checked={!!selected[c.id]} onChange={()=>toggleOne(c.id)} /></td>
                    <td className="clickable fw-bold" onClick={()=>setShowDetail(c)}>{c.name}</td>
                    <td><span className={`badge ${typeBadge[c.customer_type]||'badge-gray'}`}>{typeLabel[c.customer_type]||'-'}</span></td>
                    <td>
                      {c.contact_name ? (
                        <span className="clickable" style={{ color:'#3b82f6', textDecoration:'underline', cursor:'pointer' }}
                          title="발행된 견적 목록 보기"
                          onClick={()=>openEstimatesModal(c)}>{c.contact_name}</span>
                      ) : '-'}
                    </td>
                    <td>{c.phone}</td>
                    <td style={{textAlign:'center'}}>{c.estimate_count||0}</td>
                    <td>{c.last_activity?.slice(0,10)||'-'}</td>
                    <td><div className="btn-group">
                      <button className="btn btn-outline btn-sm" onClick={()=>setShowDetail(c)}>보기</button>
                      {canPerformAction('고객 등록/편집') && <button className="btn btn-outline btn-sm" onClick={()=>openEdit(c)}>편집</button>}
                      {canPerformAction('고객 삭제') && <button className="btn btn-danger btn-sm" onClick={()=>{setDeleteTarget(c);setDeletePassword('');}}>삭제</button>}
                    </div></td>
                  </tr>
                ))}
                {showDeleted && deletedCustomers.length > 0 && (
                  <tr style={{background:'#fef2f2'}}><td colSpan={9} style={{padding:'8px 10px',borderTop:'2px solid #fecaca'}}>
                    <div style={{display:'flex',alignItems:'center',gap:10}}>
                      <input type="checkbox" checked={allDeletedSelected} onChange={toggleAllDeleted} />
                      <span style={{fontSize:12,fontWeight:700,color:'#ef4444'}}>삭제예정 ({deletedCustomers.length}건)</span>
                      {someDeletedSelected && <button className="btn btn-danger btn-sm" style={{marginLeft:'auto'}} onClick={()=>{setShowBulkPermanent(true);setBulkPermanentPw('');}}>일괄 즉시삭제 ({selectedDeletedCount})</button>}
                    </div>
                  </td></tr>
                )}
                {showDeleted && deletedCustomers.map(c=>(
                  <tr key={`del-${c.id}`} style={{background:'#fef2f2',color:'#94a3b8'}}>
                    <td><input type="checkbox" checked={!!selectedDeleted[c.id]} onChange={()=>toggleOneDeleted(c.id)} /></td>
                    <td style={{textDecoration:'line-through'}}>{c.name}</td>
                    <td><span className={`badge ${typeBadge[c.customer_type]||'badge-gray'}`}>{typeLabel[c.customer_type]||'-'}</span></td>
                    <td style={{textDecoration:'line-through'}}>{c.contact_name||'-'}</td>
                    <td>{c.phone||'-'}</td>
                    <td style={{textAlign:'center',color:'#ef4444',fontWeight:600}}>삭제예정</td>
                    <td>{c.deleted_at?.slice(0,10)}</td>
                    <td><div className="btn-group">
                      <button className="btn btn-outline btn-sm" onClick={()=>handleRestore(c)}>복원</button>
                      <button className="btn btn-danger btn-sm" onClick={()=>{setPermanentTarget(c);setPermanentPassword('');}}>즉시삭제</button>
                    </div></td>
                  </tr>
                ))}
                {filtered.filter(c=>!c.is_incomplete).length===0 && !showDeleted && <tr><td colSpan={8} style={{textAlign:'center',padding:32,color:'#94a3b8'}}>등록된 고객이 없습니다.</td></tr>}
              </tbody>
            </table>
          </div>
        )}
        <Pager />
      </div>

      {/* 고객 등록/편집 모달 */}
      {showForm && (
        <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,0.4)',zIndex:100,display:'flex',alignItems:'center',justifyContent:'center'}}>
          <div style={{background:'#fff',borderRadius:14,padding:28,width:520,maxHeight:'85vh',overflowY:'auto'}}>
            <div style={{fontSize:16,fontWeight:700,marginBottom:16,paddingBottom:10,borderBottom:'1px solid #e2e8f0',display:'flex',justifyContent:'space-between'}}>
              <span>{editing ? `고객 편집 — ${editing.name}` : '고객 등록'}</span>
              <span style={{cursor:'pointer',color:'#94a3b8'}} onClick={()=>setShowForm(false)}>✕</span>
            </div>
            {formError && <div className="alert alert-danger" style={{marginBottom:12}}>{formError}</div>}
            <div className="form-group"><label className="form-label">회사명 *</label><input className="form-input" value={form.name} onChange={e=>setForm({...form,name:e.target.value})} /></div>
            <div className="form-row">
              <div className="form-group" style={{flex:1}}>
                <label className="form-label">고객 유형</label>
                <select className="form-input" value={form.customer_type} onChange={e=>setForm({...form,customer_type:e.target.value})}>
                  <option value="end_user">고객</option>
                  <option value="distributor">총판</option>
                  <option value="partner">협력사</option>
                </select>
              </div>
              <div className="form-group" style={{flex:1}}><label className="form-label">담당자</label><input className="form-input" value={form.contact_name} onChange={e=>setForm({...form,contact_name:e.target.value})} /></div>
            </div>
            <div className="form-row">
              <div className="form-group" style={{flex:1}}><label className="form-label">연락처</label><input className="form-input" value={form.phone} onChange={e=>setForm({...form,phone:e.target.value})} placeholder="02-0000-0000" /></div>
              <div className="form-group" style={{flex:1}}><label className="form-label">이메일</label><input className="form-input" value={form.email} onChange={e=>setForm({...form,email:e.target.value})} /></div>
            </div>
            <div style={{display:'flex',gap:8,justifyContent:'flex-end',marginTop:12}}>
              <button className="btn btn-outline" onClick={()=>setShowForm(false)}>취소</button>
              <button className="btn btn-primary" disabled={saving} onClick={handleSave}>{saving?'저장 중...':'저장'}</button>
            </div>
          </div>
        </div>
      )}

      {/* 담당자별 발행 견적 목록 모달 */}
      {estimatesFor && (
        <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,0.4)',zIndex:100,display:'flex',alignItems:'center',justifyContent:'center'}} onClick={()=>setEstimatesFor(null)}>
          <div onClick={e=>e.stopPropagation()} style={{background:'#fff',borderRadius:14,padding:24,width:600,maxHeight:'85vh',overflowY:'auto'}}>
            <div style={{fontSize:16,fontWeight:700,marginBottom:14,paddingBottom:10,borderBottom:'1px solid #e2e8f0',display:'flex',justifyContent:'space-between'}}>
              <span>{estimatesFor.name} · {estimatesFor.contact_name} — 발행 견적</span>
              <span style={{cursor:'pointer',color:'#94a3b8'}} onClick={()=>setEstimatesFor(null)}>✕</span>
            </div>
            {estimatesLoading ? (
              <div style={{padding:30,textAlign:'center',color:'#94a3b8',fontSize:13}}>로딩 중…</div>
            ) : estimatesList.length === 0 ? (
              <div style={{padding:30,textAlign:'center',color:'#94a3b8',fontSize:13}}>발행된 견적이 없습니다.</div>
            ) : (
              <table style={{width:'100%',fontSize:12}}>
                <thead><tr style={{background:'#f8fafc'}}>
                  <th style={{padding:'8px 10px',textAlign:'left'}}>견적번호</th>
                  <th style={{padding:'8px 10px',textAlign:'left'}}>일자</th>
                  <th style={{padding:'8px 10px',textAlign:'left'}}>작성자</th>
                  <th style={{padding:'8px 10px',textAlign:'left'}}>사업명</th>
                  <th style={{padding:'8px 10px',textAlign:'right'}}>금액</th>
                  <th style={{padding:'8px 10px',textAlign:'right'}}>접근</th>
                </tr></thead>
                <tbody>
                  {estimatesList.map(e => {
                    const canOpen = e.has_access === true;
                    return (
                      <tr key={e.id} style={{borderBottom:'1px solid #f1f5f9',cursor: canOpen?'pointer':'default'}}
                        className={canOpen?'clickable':undefined}
                        onClick={canOpen ? ()=>{ setEstimatesFor(null); navigate(`/product-estimate/new?id=${e.id}`); } : undefined}>
                        <td style={{padding:'8px 10px',color: canOpen?'#3b82f6':'#94a3b8',fontWeight:600}}>
                          {e.estimate_number || e.draft_id}
                          {e.version > 1 && <span style={{marginLeft:6,fontSize:10,color:'#64748b'}}>v{e.version}</span>}
                        </td>
                        <td style={{padding:'8px 10px'}}>{(e.estimate_date||'').slice(0,10)||'-'}</td>
                        <td style={{padding:'8px 10px'}}>{e.author_name||'-'}</td>
                        <td style={{padding:'8px 10px',maxWidth:200,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{e.project_name||'-'}</td>
                        <td style={{padding:'8px 10px',textAlign:'right',fontWeight:600}}>
                          ₩{Number((e.vat_separate !== false) ? (e.subtotal||0) : (e.total_amount||0)).toLocaleString()}
                          <span style={{marginLeft:6,fontSize:10,color:'#94a3b8',fontWeight:400}}>{(e.vat_separate !== false) ? '(VAT별도)' : '(VAT포함)'}</span>
                        </td>
                        <td style={{padding:'8px 10px',textAlign:'right'}} onClick={ev=>ev.stopPropagation()}>
                          {canOpen ? <span className="badge badge-blue" style={{fontSize:10}}>열람가능</span>
                            : e.share_request_status === 'pending'
                              ? <span className="badge badge-yellow" style={{fontSize:10}}>요청 대기 중</span>
                              : e.share_request_status === 'rejected'
                                ? <button className="btn btn-outline btn-sm" style={{padding:'2px 8px',fontSize:10}} onClick={()=>openShareRequest(e)}>↻ 다시 요청</button>
                                : <button className="btn btn-primary btn-sm" style={{padding:'2px 8px',fontSize:10}} onClick={()=>openShareRequest(e)}>공유 요청</button>}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
            <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginTop:14,paddingTop:10,borderTop:'1px solid #e2e8f0'}}>
              <span style={{fontSize:11,color:'#94a3b8'}}>총 {estimatesList.length}건 (삭제된 견적 제외)</span>
              <button className="btn btn-outline" onClick={()=>setEstimatesFor(null)}>닫기</button>
            </div>
          </div>
        </div>
      )}

      {/* 공유 요청 모달 */}
      {shareReqTarget && (
        <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,0.4)',zIndex:110,display:'flex',alignItems:'center',justifyContent:'center'}} onClick={()=>setShareReqTarget(null)}>
          <div onClick={ev=>ev.stopPropagation()} style={{background:'#fff',borderRadius:14,padding:24,width:420}}>
            <div style={{fontSize:16,fontWeight:700,marginBottom:12,paddingBottom:10,borderBottom:'1px solid #e2e8f0',display:'flex',justifyContent:'space-between'}}>
              <span>견적서 공유 요청</span>
              <span style={{cursor:'pointer',color:'#94a3b8'}} onClick={()=>setShareReqTarget(null)}>✕</span>
            </div>
            <div style={{fontSize:13,marginBottom:12,color:'#475569',lineHeight:1.6}}>
              <div><strong>견적번호</strong> · {shareReqTarget.estimate_number}</div>
              <div><strong>작성자</strong> · {shareReqTarget.author_name || '알 수 없음'}</div>
            </div>
            <div className="form-group">
              <label className="form-label">메시지 (선택)</label>
              <textarea className="form-input" rows={3} value={shareReqMsg} onChange={e=>setShareReqMsg(e.target.value)} placeholder="요청 사유나 용도를 적어주세요" />
            </div>
            <div className="alert alert-info" style={{fontSize:12,marginBottom:12}}>
              작성자가 승인하면 자동으로 열람 권한이 부여됩니다.
            </div>
            <div style={{display:'flex',gap:8,justifyContent:'flex-end'}}>
              <button className="btn btn-outline" onClick={()=>setShareReqTarget(null)}>취소</button>
              <button className="btn btn-primary" disabled={shareReqSubmitting} onClick={submitShareRequest}>{shareReqSubmitting?'전송 중...':'요청 보내기'}</button>
            </div>
          </div>
        </div>
      )}

      {/* 고객 상세 보기 모달 */}
      {showDetail && (
        <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,0.4)',zIndex:100,display:'flex',alignItems:'center',justifyContent:'center'}}>
          <div style={{background:'#fff',borderRadius:14,padding:28,width:480}}>
            <div style={{fontSize:16,fontWeight:700,marginBottom:16,paddingBottom:10,borderBottom:'1px solid #e2e8f0',display:'flex',justifyContent:'space-between'}}>
              <span>{showDetail.name}</span>
              <span style={{cursor:'pointer',color:'#94a3b8'}} onClick={()=>setShowDetail(null)}>✕</span>
            </div>
            <table style={{width:'100%',fontSize:13}}>
              <tbody>
                <tr><td style={{color:'#64748b',padding:'6px 12px 6px 0',width:100}}>유형</td><td><span className={`badge ${typeBadge[showDetail.customer_type]||'badge-gray'}`}>{typeLabel[showDetail.customer_type]||'-'}</span></td></tr>
                <tr><td style={{color:'#64748b',padding:'6px 12px 6px 0'}}>담당자</td><td>{showDetail.contact_name||'-'}</td></tr>
                <tr><td style={{color:'#64748b',padding:'6px 12px 6px 0'}}>연락처</td><td>{showDetail.phone||'-'}</td></tr>
                <tr><td style={{color:'#64748b',padding:'6px 12px 6px 0'}}>이메일</td><td>{showDetail.email||'-'}</td></tr>
                <tr><td style={{color:'#64748b',padding:'6px 12px 6px 0'}}>견적건수</td><td>{showDetail.estimate_count||0}건</td></tr>
                <tr><td style={{color:'#64748b',padding:'6px 12px 6px 0'}}>등록일</td><td>{showDetail.created_at?.slice(0,10)}</td></tr>
              </tbody>
            </table>
            <div style={{display:'flex',gap:8,justifyContent:'flex-end',marginTop:16}}>
              {canPerformAction('고객 등록/편집') && <button className="btn btn-outline" onClick={()=>{setShowDetail(null);openEdit(showDetail);}}>편집</button>}
              <button className="btn btn-outline" onClick={()=>setShowDetail(null)}>닫기</button>
            </div>
          </div>
        </div>
      )}
      {/* 삭제 확인 모달 */}
      {deleteTarget && (
        <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,0.4)',zIndex:100,display:'flex',alignItems:'center',justifyContent:'center'}}>
          <div style={{background:'#fff',borderRadius:14,padding:28,width:400}}>
            <div style={{fontSize:16,fontWeight:700,marginBottom:16,paddingBottom:10,borderBottom:'1px solid #e2e8f0'}}>고객 삭제</div>
            <div style={{fontSize:13,marginBottom:16,color:'#475569'}}>
              <strong>"{deleteTarget.name}"</strong> 고객을 삭제하시겠습니까?<br/>
              <span style={{fontSize:11,color:'#94a3b8'}}>7일 후 완전 삭제됩니다. 비밀번호를 입력하세요.</span>
            </div>
            <div className="form-group">
              <label className="form-label">비밀번호</label>
              <input className="form-input" type="password" placeholder="현재 계정 비밀번호" value={deletePassword}
                onChange={e=>setDeletePassword(e.target.value)} onKeyDown={e=>{if(e.key==='Enter'&&deletePassword)handleDelete();}} />
            </div>
            <div style={{display:'flex',gap:8,justifyContent:'flex-end'}}>
              <button className="btn btn-outline" onClick={()=>{setDeleteTarget(null);setDeletePassword('');}}>취소</button>
              <button className="btn btn-danger" disabled={!deletePassword} style={{background:'#ef4444',color:'#fff',border:'none',opacity:deletePassword?1:0.5}} onClick={handleDelete}>삭제</button>
            </div>
          </div>
        </div>
      )}
      {/* 즉시삭제 확인 모달 */}
      {permanentTarget && (
        <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,0.4)',zIndex:100,display:'flex',alignItems:'center',justifyContent:'center'}}>
          <div style={{background:'#fff',borderRadius:14,padding:28,width:400}}>
            <div style={{fontSize:16,fontWeight:700,marginBottom:16,paddingBottom:10,borderBottom:'1px solid #e2e8f0',color:'#ef4444'}}>즉시 삭제</div>
            <div style={{fontSize:13,marginBottom:16,color:'#475569'}}>
              <strong>"{permanentTarget.name}"</strong> 고객을 완전히 삭제합니다.<br/>
              <span style={{fontSize:11,color:'#ef4444'}}>이 작업은 되돌릴 수 없습니다. 비밀번호를 입력하세요.</span>
            </div>
            <div className="form-group">
              <label className="form-label">비밀번호</label>
              <input className="form-input" type="password" placeholder="현재 계정 비밀번호" value={permanentPassword}
                onChange={e=>setPermanentPassword(e.target.value)} onKeyDown={e=>{if(e.key==='Enter'&&permanentPassword)handlePermanentDelete();}} />
            </div>
            <div style={{display:'flex',gap:8,justifyContent:'flex-end'}}>
              <button className="btn btn-outline" onClick={()=>{setPermanentTarget(null);setPermanentPassword('');}}>취소</button>
              <button className="btn btn-danger" disabled={!permanentPassword} style={{background:'#ef4444',color:'#fff',border:'none',opacity:permanentPassword?1:0.5}} onClick={handlePermanentDelete}>완전 삭제</button>
            </div>
          </div>
        </div>
      )}
      {/* 일괄 즉시삭제 모달 */}
      {showBulkPermanent && (
        <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,0.4)',zIndex:100,display:'flex',alignItems:'center',justifyContent:'center'}}>
          <div style={{background:'#fff',borderRadius:14,padding:28,width:400}}>
            <div style={{fontSize:16,fontWeight:700,marginBottom:16,paddingBottom:10,borderBottom:'1px solid #e2e8f0',color:'#ef4444'}}>일괄 즉시 삭제</div>
            <div style={{fontSize:13,marginBottom:16,color:'#475569'}}>{selectedDeletedCount}건을 완전히 삭제합니다.<br/><span style={{fontSize:11,color:'#ef4444'}}>이 작업은 되돌릴 수 없습니다.</span></div>
            <div className="form-group">
              <label className="form-label">비밀번호</label>
              <input className="form-input" type="password" placeholder="현재 계정 비밀번호" value={bulkPermanentPw}
                onChange={e=>setBulkPermanentPw(e.target.value)} onKeyDown={e=>{if(e.key==='Enter'&&bulkPermanentPw)handleBulkPermanent();}} />
            </div>
            <div style={{display:'flex',gap:8,justifyContent:'flex-end'}}>
              <button className="btn btn-outline" onClick={()=>{setShowBulkPermanent(false);setBulkPermanentPw('');}}>취소</button>
              <button className="btn btn-danger" disabled={!bulkPermanentPw} style={{background:'#ef4444',color:'#fff',border:'none',opacity:bulkPermanentPw?1:0.5}} onClick={handleBulkPermanent}>완전 삭제</button>
            </div>
          </div>
        </div>
      )}

      {/* 일괄 삭제 모달 */}
      {showBulkDelete && (
        <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,0.4)',zIndex:100,display:'flex',alignItems:'center',justifyContent:'center'}}>
          <div style={{background:'#fff',borderRadius:14,padding:28,width:400}}>
            <div style={{fontSize:16,fontWeight:700,marginBottom:16,paddingBottom:10,borderBottom:'1px solid #e2e8f0'}}>일괄 삭제</div>
            <div style={{fontSize:13,marginBottom:16,color:'#475569'}}>{selectedCount}건의 고객을 삭제 예정으로 전환합니다. 비밀번호를 입력하세요.</div>
            <div className="form-group">
              <label className="form-label">비밀번호</label>
              <input className="form-input" type="password" placeholder="현재 계정 비밀번호" value={bulkDeletePw}
                onChange={e=>setBulkDeletePw(e.target.value)} onKeyDown={e=>{if(e.key==='Enter'&&bulkDeletePw)handleBulkDelete();}} />
            </div>
            <div style={{display:'flex',gap:8,justifyContent:'flex-end'}}>
              <button className="btn btn-outline" onClick={()=>{setShowBulkDelete(false);setBulkDeletePw('');}}>취소</button>
              <button className="btn btn-danger" disabled={!bulkDeletePw} style={{background:'#ef4444',color:'#fff',border:'none',opacity:bulkDeletePw?1:0.5}} onClick={handleBulkDelete}>삭제</button>
            </div>
          </div>
        </div>
      )}

      {/* 일괄 유형 변경 모달 */}
      {showBulkType && (
        <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,0.4)',zIndex:100,display:'flex',alignItems:'center',justifyContent:'center'}}>
          <div style={{background:'#fff',borderRadius:14,padding:28,width:400}}>
            <div style={{fontSize:16,fontWeight:700,marginBottom:16,paddingBottom:10,borderBottom:'1px solid #e2e8f0'}}>유형 일괄 변경</div>
            <div style={{fontSize:13,marginBottom:16,color:'#475569'}}>{selectedCount}건의 고객 유형을 변경합니다.</div>
            <div className="form-group">
              <label className="form-label">변경할 유형</label>
              <select className="form-input" value={bulkTypeValue} onChange={e=>setBulkTypeValue(e.target.value)}>
                <option value="end_user">고객</option>
                <option value="distributor">총판</option>
                <option value="partner">협력사</option>
              </select>
            </div>
            <div style={{display:'flex',gap:8,justifyContent:'flex-end'}}>
              <button className="btn btn-outline" onClick={()=>setShowBulkType(false)}>취소</button>
              <button className="btn btn-primary" onClick={handleBulkTypeChange}>변경</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
